import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { motion } from "motion/react";
import { Menu, X, ArrowLeft } from "lucide-react";

import NotificationBell from "@/components/NotificationBell/NotificationBell";
import { APP_BAR_TOOLBAR_ICON_BUTTON_SX } from "@/theme/appTheme";

import { APP_BAR_CHROME_ICON_BUTTON_SX, APP_BAR_GLASS_MAP_ICON_BUTTON_SX, APP_BAR_GLASS_MAP_TITLE_SX } from "./constants";

/**
 * Default authenticated app bar row: back or hamburger, gradient title, middle slots, right slots, bell, legacy actions.
 * Legacy icon actions render only when `rightComponents` is empty (backward compatibility with `appBarStore.actions`).
 * @param {object} props
 * @param {{ onClick: () => void, icon?: import("react").ReactNode } | null | undefined} props.backAction - When set, replaces the mobile menu button.
 * @param {boolean} props.showMobileMenu - When true and no `backAction`, shows the hamburger / close control.
 * @param {boolean} props.isMobileMenuOpen - Drives menu icon and `aria-expanded`.
 * @param {() => void} props.onMobileMenuToggle
 * @param {string | null | undefined} props.title - Page title; also selects `h1` vs neutral `span` semantics.
 * @param {import("react").ReactNode[]} props.middleComponents - Inline slots between title and trailing cluster.
 * @param {string[]} props.middleKeys - React keys aligned with `middleComponents`.
 * @param {import("react").ReactNode[]} props.rightComponents - Trailing actions before the notification bell.
 * @param {string[]} props.rightKeys - React keys aligned with `rightComponents`.
 * @param {{ onClick: () => void, icon: import("react").ReactNode, color?: string }[]} props.legacyActions - Shown only if `rightComponents` is empty.
 * @param {string[]} props.legacyKeys - React keys aligned with `legacyActions`.
 * @param {boolean} [props.glassMapChrome=false] - Dark frosted controls for mobile map overlay pages.
 * @returns {import("react").JSX.Element}
 */
export default function StandardAppBarToolbar({
  backAction,
  showMobileMenu,
  isMobileMenuOpen,
  onMobileMenuToggle,
  title,
  middleComponents,
  middleKeys,
  rightComponents,
  rightKeys,
  legacyActions,
  legacyKeys,
  glassMapChrome = false,
}) {
  const chromeIconButtonSx = glassMapChrome ? APP_BAR_GLASS_MAP_ICON_BUTTON_SX : APP_BAR_CHROME_ICON_BUTTON_SX;
  const iconColor = glassMapChrome ? "currentColor" : "#9D6AC4";

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {backAction ? (
          <IconButton onClick={backAction.onClick} aria-label="Go back" sx={chromeIconButtonSx}>
            {backAction.icon ?? <ArrowLeft size={20} color={iconColor} />}
          </IconButton>
        ) : (
          showMobileMenu && (
            <IconButton
              onClick={onMobileMenuToggle}
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              sx={chromeIconButtonSx}
            >
              <motion.div animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}>
                {isMobileMenuOpen ? <X size={20} color={iconColor} /> : <Menu size={20} color={iconColor} />}
              </motion.div>
            </IconButton>
          )
        )}

        <Typography
          component={title ? "h1" : "span"}
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.375rem", md: "1.375rem" },
            lineHeight: 1.25,
            ml: { xs: 0, sm: "4px" },
            fontWeight: 700,
            fontFamily: "'Inter', sans-serif",
            background: "linear-gradient(135deg, #7B4FA0 0%, #9D6AC4 50%, #D67A56 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            userSelect: "none",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ...(glassMapChrome ? APP_BAR_GLASS_MAP_TITLE_SX : {}),
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minWidth: 0,
          ml: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          {middleComponents.map((component, i) => (
            <Box key={middleKeys[i]} sx={{ display: "flex", alignItems: "center" }}>
              {component}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {rightComponents.map((component, i) => (
            <Box key={rightKeys[i]} sx={{ display: "flex", alignItems: "center" }}>
              {component}
            </Box>
          ))}

          <NotificationBell iconButtonSx={glassMapChrome ? APP_BAR_GLASS_MAP_ICON_BUTTON_SX : undefined} />

          {!rightComponents.length && (
            <>
              {legacyActions.map((action, i) => (
                <IconButton
                  key={legacyKeys[i]}
                  onClick={action.onClick}
                  sx={{
                    ...APP_BAR_TOOLBAR_ICON_BUTTON_SX,
                    color: action.color || "#9D6AC4",
                  }}
                >
                  {action.icon}
                </IconButton>
              ))}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
